import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenantContext } from '../contexts/TenantContext';
import AgentAvatar from './AgentAvatar';
import RoleSlotCard, { type RoleSlot } from './RoleSlotCard';
import AddDelegateModal from './AddDelegateModal';

interface Props {
  projectId: string;
}

export interface ProjectMemberRow {
  id:              string;
  userId:          string;
  userName:        string;
  userEmail:       string;
  role:            string;
  memberType:      'PRIMARY' | 'DELEGATE';
  primaryMemberId: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  PM: 'Product Manager',
  ARCHITECT: 'Architect',
  TECH_LEAD: 'Tech Lead',
  DEVELOPER: 'Developer',
  QA: 'QA Engineer',
  DEVOPS: 'DevOps Engineer',
  STAKEHOLDER: 'Stakeholder',
  PRODUCT_MANAGER: 'Product Manager',
  LEAD_ENGINEER: 'Lead Engineer',
  BACKEND_ENGINEER: 'Backend Engineer',
  FRONTEND_ENGINEER: 'Frontend Engineer',
  QA_ENGINEER: 'QA Engineer',
  DEVOPS_ENGINEER: 'DevOps Engineer',
  DESIGNER: 'Designer',
};

const DELIVERY_ROLES = new Set([
  'PRODUCT_MANAGER', 'ARCHITECT', 'TECH_LEAD', 'DEVELOPER', 'QA_ENGINEER',
  'DEVOPS', 'STAKEHOLDER', 'PM', 'QA',
]);

function badgeStyle(kind: 'PRIMARY' | 'DELEGATE'): React.CSSProperties {
  return kind === 'PRIMARY'
    ? { background: 'rgba(16,185,129,0.12)', color: 'var(--success)' }
    : { background: 'rgba(124,58,237,0.1)', color: '#7c3aed' };
}

function MemberBadge({ kind }: { kind: 'PRIMARY' | 'DELEGATE' }) {
  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
        fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
        ...badgeStyle(kind),
      }}
    >
      {kind === 'PRIMARY' ? 'Primary' : 'Delegate'}
    </span>
  );
}

/**
 * #2733 — replaces TeamPanel in the project dashboard's "Team" tab (same tab
 * key, ?tab=team — GateReportPanel's "Assign Architect" link and similar
 * deep links keep working unchanged). Superset of TeamPanel: reuses
 * RoleSlotCard as-is for UNBOUND/PENDING/EXPIRED roles (invite/resend/cancel
 * already work well there), and adds its own per-row rendering only for
 * BOUND roles, where it shows every project_members row instead of
 * collapsing to one occupant — the visibility gap this issue exists to close.
 */
export default function ProjectMembersPanel({ projectId }: Props) {
  const { user: currentUser } = useAuth();
  const { currentTenant } = useTenantContext();
  const isTenantAdmin = currentTenant?.role === 'TENANT_ADMIN';

  const [slots, setSlots] = useState<RoleSlot[]>([]);
  const [members, setMembers] = useState<ProjectMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState<{ role: string; delegateId: string; delegateName: string } | null>(null);
  const [addDelegateRole, setAddDelegateRole] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(() => {
    Promise.all([
      apiFetch<RoleSlot[]>(`/projects/${projectId}/roles`),
      apiFetch<ProjectMemberRow[]>(`/projects/${projectId}/members`),
    ])
      .then(([slotData, memberData]) => {
        setSlots(slotData);
        setMembers(memberData);
        setLoading(false);
        setError('');
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load project members');
        setLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    refresh();
    intervalRef.current = setInterval(refresh, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh]);

  const handleRemove = async (row: ProjectMemberRow) => {
    setBusyId(row.id);
    setRowError(null);
    try {
      await apiFetch(`/projects/${projectId}/members/${row.userId}?role=${encodeURIComponent(row.role)}`, { method: 'DELETE' });
      setConfirmRemoveId(null);
      refresh();
    } catch (err) {
      setRowError({ id: row.id, message: err instanceof Error ? err.message : 'Failed to remove member' });
      setConfirmRemoveId(null);
    } finally {
      setBusyId(null);
    }
  };

  const handleSwitch = async () => {
    if (!confirmSwitch) return;
    const { role, delegateId } = confirmSwitch;
    setBusyId(delegateId);
    setRowError(null);
    try {
      await apiFetch(`/projects/${projectId}/members/switch-primary`, {
        method: 'POST',
        body: JSON.stringify({ role, newPrimaryMemberId: delegateId }),
      });
      setConfirmSwitch(null);
      refresh();
    } catch (err) {
      setRowError({ id: delegateId, message: err instanceof Error ? err.message : 'Failed to switch primary' });
      setConfirmSwitch(null);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} className="animate-pulse">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} style={{ height: 68, borderRadius: 'var(--radius-md)', background: 'var(--surface-alt)', border: '1px solid var(--border)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--error)' }}>{error}</p>
      </div>
    );
  }

  const boundRoles = new Set(members.map(m => m.role));
  const unboundSlots = slots.filter(s => !boundRoles.has(s.role));

  const membersByRole = new Map<string, ProjectMemberRow[]>();
  for (const m of members) {
    const list = membersByRole.get(m.role) ?? [];
    list.push(m);
    membersByRole.set(m.role, list);
  }

  const roleRowStyle: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', gap: 8,
    padding: '12px 16px', borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)', background: 'var(--surface)',
  };

  const renderMemberGroup = (role: string, rows: ProjectMemberRow[]) => {
    const primary = rows.find(r => r.memberType === 'PRIMARY');
    const delegates = rows.filter(r => r.memberType === 'DELEGATE');
    const roleLabel = ROLE_LABELS[role] ?? role;

    return (
      <div key={role} style={roleRowStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
            {roleLabel}
          </p>
          {isTenantAdmin && primary && (
            <button
              onClick={() => setAddDelegateRole(role)}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                color: 'var(--primary)', textDecoration: 'underline',
              }}
            >
              + Add delegate
            </button>
          )}
        </div>

        {[...(primary ? [primary] : []), ...delegates].map(row => {
          const isSelf = !!currentUser && row.userId === currentUser.id;
          return (
            <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingLeft: row.memberType === 'DELEGATE' ? 20 : 0 }}>
              <AgentAvatar agentType={role.toLowerCase()} size={28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.userName} · {row.userEmail}
                  </p>
                  <MemberBadge kind={row.memberType} />
                </div>

                {rowError?.id === row.id && (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', margin: '4px 0 0' }}>
                    {rowError.message}
                  </p>
                )}

                {confirmRemoveId === row.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 0' }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)' }}>
                      {isSelf ? 'Remove your access?' : `Remove ${row.userName}?`}
                    </span>
                    <button
                      onClick={() => void handleRemove(row)}
                      disabled={busyId === row.id}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', textDecoration: 'underline', fontWeight: 600 }}
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmRemoveId(null)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', textDecoration: 'underline' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : confirmSwitch?.delegateId === row.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 0' }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--warning)' }}>
                      Make {row.userName} primary? {primary?.userName} becomes a delegate.
                    </span>
                    <button
                      onClick={() => void handleSwitch()}
                      disabled={busyId === row.id}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--warning)', textDecoration: 'underline', fontWeight: 600 }}
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmSwitch(null)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', textDecoration: 'underline' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 0' }}>
                    <button
                      onClick={() => setConfirmRemoveId(row.id)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', textDecoration: 'underline' }}
                    >
                      Remove
                    </button>
                    {isTenantAdmin && row.memberType === 'DELEGATE' && (
                      <button
                        onClick={() => setConfirmSwitch({ role, delegateId: row.id, delegateName: row.userName })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)', textDecoration: 'underline' }}
                      >
                        Make primary
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 12,
  };

  const boundGroups = [...membersByRole.entries()];
  const deliveryBound = boundGroups.filter(([role]) => DELIVERY_ROLES.has(role));
  const businessBound = boundGroups.filter(([role]) => !DELIVERY_ROLES.has(role));
  const deliveryUnbound = unboundSlots.filter(s => DELIVERY_ROLES.has(s.role));
  const businessUnbound = unboundSlots.filter(s => !DELIVERY_ROLES.has(s.role));

  return (
    <div style={{ padding: '24px 32px', maxWidth: 640 }}>
      {members.length === 0 && slots.length === 0 ? (
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
          No role slots defined for this project.
        </p>
      ) : (
        <>
          {(deliveryBound.length > 0 || deliveryUnbound.length > 0) && (
            <div style={{ marginBottom: 24 }}>
              <div style={sectionLabelStyle}>Delivery Team</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {deliveryBound.map(([role, rows]) => renderMemberGroup(role, rows))}
                {deliveryUnbound.map(slot => (
                  <RoleSlotCard key={slot.role} slot={slot} projectId={projectId} onInviteSuccess={refresh} userRoleCount={0} />
                ))}
              </div>
            </div>
          )}

          {(businessBound.length > 0 || businessUnbound.length > 0) && (
            <div>
              <div style={sectionLabelStyle}>Client &amp; Business Stakeholders</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {businessBound.map(([role, rows]) => renderMemberGroup(role, rows))}
                {businessUnbound.map(slot => (
                  <RoleSlotCard key={slot.role} slot={slot} projectId={projectId} onInviteSuccess={refresh} userRoleCount={0} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {addDelegateRole && currentTenant && (
        <AddDelegateModal
          projectId={projectId}
          tenantId={currentTenant.id}
          role={addDelegateRole}
          roleLabel={ROLE_LABELS[addDelegateRole] ?? addDelegateRole}
          excludeUserIds={(membersByRole.get(addDelegateRole) ?? []).map(r => r.userId)}
          onClose={() => setAddDelegateRole(null)}
          onSuccess={refresh}
        />
      )}
    </div>
  );
}
